/* eslint-disable operator-assignment */
/* eslint-disable consistent-return */
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { transparentize } from 'polished';
//
import colors from 'theme/colors';
import {
  IDataPoint,
  ILineGraphItem,
  ISummaryHistory,
  ISummaryForecast,
  ISummaryProduct,
} from 'store/deal-evaluation/types';
import {
  getMaxValueFromArrayOfLines,
  getMinValueFromArrayOfLines,
  calculateBalancedHistory,
} from 'components/visuals/multi-line-graph/helpers';
import { lineGraphClassName } from 'components/visuals/multi-line-graph/constants';
import {
  multiLineGraphLine,
  multiLineGraphContainer,
  multiLineGraphLabel,
} from 'components/visuals/multi-line-graph/test-ids';
import { formatNumber } from 'lib/helpers/currency';
import { EDateRange, EDisplay } from 'components/deal-results/product-items';
import {
  Control,
  ControlGroup,
  Controls,
  BubblyContainer,
  PriceContainer,
  PriceLabel,
} from 'components/visuals/_common';
import ToggleButton from 'components/buttons/toggle';
import MultiToggleButton from 'components/buttons/multi-toggle';
import lineColors from 'components/visuals/multi-line-graph/colors';
import bgColors from 'components/visuals/multi-line-graph/bgcolors';
import { generateLegendText, measureMaxTextWidth } from 'lib/helpers/visuals';
import { getActiveIndexForMultiToggleButton } from 'components/deal-results/product-items/helpers';
import BubblyCheckboxButton from 'components/buttons/bubbly-checkbox';
import font from 'theme/visuals';
import {
  timeFormat,
  Selection,
  ScaleTime,
  ScaleLinear,
  timeParse,
  line,
  curveMonotoneX,
  range,
  selectAll,
  select,
  scaleTime,
  axisLeft,
  scaleLinear,
  axisBottom,
  symbol,
  symbolTriangle,
  mouse,
  event,
  area,
} from 'd3';
import { renderNoDataGraph } from './no-data-graph';

/**
 * MultiLineGraph
 *
 * Displays an array of line graphs with both historical, and forecast data
 * including the shaded p5-p95 areas
 */

interface IMultiLineGraphProps {
  productSummary: ISummaryProduct;
  periodStart: Date;
  periodEnd: Date;
  pricePerUnitLabel: string;
  dealName: string;
  selectedForecastLabel: string;
  selectedCostLabel: string;
  productItemId: string;
}

const contractWithoutOptions = 'Contract w/o Options';
const strokeWidth = 'stroke-width';
const strokeDashArray = 'stroke-dasharray';
const dataTestId = 'data-testid';
const textAnchor = 'text-anchor';
const pointerEvents = 'pointer-events';
const aspectRatio = 0.667;
const fontFamily = 'font-family';
// Date formatters
const xAxisDateFormat = '%m/%Y';
const dateFormatter = timeFormat(xAxisDateFormat);
const margin = {
  top: 32,
  right: 32,
  bottom: 128,
  left: 72,
};
const verticalPaddingMultiplier = 0.1; // 10% of height added
const legendStrokeWidth = 4;
const strokeWidthMaxSize = 3;
const strokeWidthMinSize = 2;
const priceValueTextNumber = 100;
const otherBatnaNumber = 2;
const captionBottomNumber = 8;
const svgAttrNumber = 10;
const minWindowHeight = 192;
const tickPaddingValue = 6;
const tickArgumentsValue = 7;
const tooltipD3Size = 64;
const tooltipTransparentizeValue = 0.3;
const svgStrokeWidthValue = 0.5;
const svgTransparentizeValue = 0.9;
const svgYAttrValue = 20;
const svgYAttrMaxValue = 36;
const tooltipWidthAttrValue = 80;
const tooltipHeightAttr = 50;
const tooltipXAttrValue = -40;
const tooltipOuterYAttrValue = -61;
const tooltipInnerFirstYAttrValue = -42;
const tooltipInnerSecondYAttrValue = -24;

function drawLegend(
  svgParent: Selection<SVGGElement, unknown, null, undefined>,
  graphItems: ILineGraphItem[],
  legendBottom: number,
  legendWidth: number,
  showRange: boolean
): number {
  const fontSize = 12;
  const leftPadding = 10;
  const rightPadding = 30;
  const coloredLineWidth = 20;
  const textStartOffset = coloredLineWidth + leftPadding;
  const lineSpacing = 20;
  const dottedLineLegend = '(dashed line is the 5% to 95% range)';
  const legendLeftOffset = 40;

  const labels: { description: string; color: string | null }[] = graphItems.map(item => ({
    description: item.priceSummary.description + (item.label === contractWithoutOptions ? ' (w/o options)' : ''),
    color: item.color,
  }));
  if (showRange) {
    labels.push({ description: dottedLineLegend, color: null });
  }
  const legend = svgParent.append('g').attr('transform', `translate(${legendLeftOffset}, 0)`);

  // Check if legend fits on one line
  const totalTextWidth = labels.reduce((acc, lbl) => acc + measureMaxTextWidth([lbl.description], fontSize), 0);
  const totalOneLineWidth =
    totalTextWidth + labels.length * (textStartOffset + leftPadding + rightPadding) - rightPadding;

  if (totalOneLineWidth < legendWidth - legendLeftOffset) {
    const legendHeights = lineSpacing;
    const legendTops = legendBottom - legendHeights;

    let posX = 0;
    labels.forEach(lbl => {
      const labelTextWidths = measureMaxTextWidth([lbl.description], fontSize);
      if (lbl.color) {
        legend
          .append('line')
          .style('stroke', lbl.color)
          .style('stroke-linecap', 'round')
          .style(strokeWidth, legendStrokeWidth)
          .attr('x1', posX)
          .attr('y1', legendTops + lineSpacing - strokeWidthMaxSize)
          .attr('x2', posX + coloredLineWidth)
          .attr('y2', legendTops + lineSpacing - strokeWidthMaxSize);
      }

      legend
        .append('text')
        .attr('x', posX + textStartOffset)
        .attr('y', legendTops + lineSpacing)
        .text(lbl.description);

      posX += textStartOffset + labelTextWidths + leftPadding + rightPadding;
    });

    return legendHeights;
  }

  // Multiline legend
  const labelTextWidth = measureMaxTextWidth(
    labels.map(lbl => lbl.description),
    fontSize
  );
  const labelTotalWidth = textStartOffset + labelTextWidth + leftPadding + rightPadding;

  const labelsPerLine = Math.floor((legendWidth - legendLeftOffset) / labelTotalWidth);
  const rows = Math.floor((labels.length - 1) / labelsPerLine) + 1;
  const legendHeight = rows * lineSpacing;
  const legendTop = legendBottom - legendHeight;

  labels.forEach((label, index) => {
    const col = index % labelsPerLine;
    const row = Math.floor(index / labelsPerLine);
    if (label.color) {
      legend
        .append('line')
        .style('stroke', label.color)
        .style('stroke-linecap', 'round')
        .style(strokeWidth, legendStrokeWidth)
        .attr('x1', col * labelTotalWidth)
        .attr('y1', legendTop + (row + 1) * lineSpacing - strokeWidthMaxSize)
        .attr('x2', col * labelTotalWidth + coloredLineWidth)
        .attr('y2', legendTop + (row + 1) * lineSpacing - strokeWidthMaxSize);
    }

    legend
      .append('text')
      .attr('x', col * labelTotalWidth + textStartOffset)
      .attr('y', legendTop + (row + 1) * lineSpacing)
      .text(label.description);
  });

  return legendHeight;
}

const processHistoricalDataBasedOnSelection = (
  activeLineGraphItems: ILineGraphItem[],
  {
    periodStart,
    periodEnd,
    dateRange,
    display,
  }: { periodStart: Date; periodEnd: Date; dateRange: EDateRange; display: EDisplay },
  svg: Selection<SVGGElement, unknown, null, undefined>,
  x: ScaleTime<number, number>,
  y: ScaleLinear<number, number>,
  width: number,
  height: number
) => {
  // Process historical data based on selection
  let activeLineGraphItemsProcessed: ILineGraphItem[] = [];
  if (dateRange === EDateRange.ALL) {
    activeLineGraphItemsProcessed = activeLineGraphItems;
  } else {
    activeLineGraphItems.forEach(lineGraphItem => {
      const selectedPriceSummary = lineGraphItem.priceSummary[display];

      if (!selectedPriceSummary) {
        renderNoDataGraph({ svg, x, y, width, height });
        return;
      }

      activeLineGraphItemsProcessed.push({
        ...lineGraphItem,
        priceSummary: {
          ...lineGraphItem.priceSummary,
          [display]: {
            ...selectedPriceSummary,
            history: calculateBalancedHistory(
              selectedPriceSummary.history,
              selectedPriceSummary.forecast.length,
              periodStart,
              periodEnd
            ),
          },
        },
      });
    });
  }
  return activeLineGraphItemsProcessed;
};

const getMultiLineGraphDimensions = (lineGraphRef: React.RefObject<HTMLDivElement>) => {
  const dimensions = { width: 900, height: 600 };
  // Calculate dimensions
  if (lineGraphRef?.current) {
    dimensions.width = lineGraphRef.current.offsetWidth;
    dimensions.height = lineGraphRef.current.offsetWidth * aspectRatio;
  }

  // Ensure that height does not bleed off screen
  const windowHeight = window.innerHeight;
  const maxWindowHeight = windowHeight - minWindowHeight;
  if (dimensions.height > maxWindowHeight) {
    dimensions.height = maxWindowHeight;
  }
  return dimensions;
};

const getTickFormat = (
  d:
    | number
    | {
        valueOf(): number;
      }
) => {
  return typeof d === 'number' ? formatNumber(d, 0) : d;
};

const applyStrokeWidthValue = (hoverLine: string, priceSummaryItem: ILineGraphItem) => {
  return hoverLine === priceSummaryItem.label ? strokeWidthMaxSize : strokeWidthMinSize;
};

const processFillLinesForP95x = (p95Data: IDataPoint[], i: number) => {
  return p95Data[i].x ?? 0;
};

const processFillLinesForP95y = (p95Data: IDataPoint[], i: number) => {
  return p95Data[i].y ?? 0;
};

const processFillLinesForP5x = (p5Data: IDataPoint[], i: number) => {
  return p5Data[i].x ?? 0;
};

const processFillLinesForP5y = (p5Data: IDataPoint[], i: number) => {
  return p5Data[i].y ?? 0;
};

const fillBackgroundColor = (background: string | undefined) => {
  return background ?? colors.background;
};

const labelOpacityOne = '0.5';
const labelOpacityTwo = '0.3';

const applyOpacityValue = (hoverLine: string, priceSummaryItem: ILineGraphItem) => {
  return hoverLine === priceSummaryItem.label ? labelOpacityOne : labelOpacityTwo;
};

const processEachLine = (
  activeLineGraphItemsProcessed: ILineGraphItem[],
  display: EDisplay,
  svg: Selection<SVGGElement, unknown, null, undefined>,
  x: ScaleTime<number, number>,
  y: ScaleLinear<number, number>,
  hoverLine: string,
  showUncertaintyRange: boolean
) => {
  const parseTime = timeParse('%m/%Y');
  // Track all lines to render
  const lineDrawFunctions: (() => void)[] = [];

  // Define line builder
  const lineBuilder = line<IDataPoint>()
    .x(d => x(d.x ?? 0)) // Add null check and fallback value
    .y(d => y(d.y ?? 0)) // Add null check and fallback value
    .curve(curveMonotoneX);
  // Min/max y-values
  let minY = 0;
  let maxY = 0;

  // Process each line
  activeLineGraphItemsProcessed.forEach(priceSummaryItem => {
    const { color, background, priceSummary } = priceSummaryItem;
    const price = priceSummary[display];
    if (!price) {
      return;
    }
    const { history, forecast } = price;

    // Process historical data
    if (!history) {
      return;
    }
    const historicalData: IDataPoint[] = history.map((h: ISummaryHistory) => ({
      x: parseTime(h.x),
      y: h.y,
    }));

    // Add historical line
    lineDrawFunctions.push(() => {
      svg
        .append('path')
        .data([historicalData])
        .attr('class', 'line')
        .attr('stroke', color)
        .attr(strokeWidth, applyStrokeWidthValue(hoverLine, priceSummaryItem))
        .attr('fill', 'none')
        .attr(dataTestId, multiLineGraphLine)
        .attr('d', lineBuilder);
    });

    // Process average forecast
    if (!forecast) {
      return;
    }
    const forecastData: IDataPoint[] = forecast.map((f: ISummaryForecast) => ({
      x: parseTime(f.x),
      y: f.ave,
    }));

    // Add last data point of historical to forecast to connect lines
    forecastData.unshift(historicalData[historicalData.length - 1]);

    // Add average forecast line
    lineDrawFunctions.push(() => {
      svg
        .append('path')
        .data([forecastData])
        .attr('class', 'line')
        .attr('stroke', color)
        .attr(strokeWidth, applyStrokeWidthValue(hoverLine, priceSummaryItem))
        .attr('fill', 'none')
        .attr(dataTestId, multiLineGraphLine)
        .attr('d', lineBuilder);
    });

    // Process p5 forecast line
    const p5Data: IDataPoint[] = forecast.map((f: ISummaryForecast) => ({
      x: parseTime(f.x),
      y: f.p5,
    }));

    // Process p95 forecast line
    const p95Data: IDataPoint[] = forecast.map((f: ISummaryForecast) => ({
      x: parseTime(f.x),
      y: f.p95,
    }));

    // Add last data point of historical to forecast to connect lines
    p5Data.unshift(historicalData[historicalData.length - 1]);

    // Add last data point of historical to forecast to connect lines
    p95Data.unshift(historicalData[historicalData.length - 1]);

    if (showUncertaintyRange) {
      // Add average forecast line
      lineDrawFunctions.push(() => {
        svg
          .append('path')
          .data([p5Data])
          .attr('class', 'line')
          .attr('stroke', color)
          .attr(strokeWidth, applyStrokeWidthValue(hoverLine, priceSummaryItem))
          .style(strokeDashArray, '5,5')
          .attr('fill', 'none')
          .attr(dataTestId, multiLineGraphLine)
          .attr('d', lineBuilder);
      });

      if (forecast) {
        // Add average forecast line
        lineDrawFunctions.push(() => {
          svg
            .append('path')
            .data([p95Data])
            .attr('class', 'line')
            .attr('stroke', color)
            .attr(strokeWidth, applyStrokeWidthValue(hoverLine, priceSummaryItem))
            .style(strokeDashArray, '5,5')
            .attr('fill', 'none')
            .attr(dataTestId, multiLineGraphLine)
            .attr('d', lineBuilder);
        });

        // if (showUncertaintyRange) {
        // Process fill lines for p5-p95
        const indicies = range(p95Data.length);
        const calcArea: any = area()
          .curve(curveMonotoneX)
          .x0((i: any) => x(processFillLinesForP95x(p95Data, i)))
          .y0((i: any) => y(processFillLinesForP95y(p95Data, i)))
          .x1((i: any) => x(processFillLinesForP5x(p5Data, i)))
          .y1((i: any) => y(processFillLinesForP5y(p5Data, i)));

        lineDrawFunctions.push(() => {
          svg
            .append('path')
            .datum(indicies)
            .attr('class', 'area')
            .attr('fill', fillBackgroundColor(background))
            .attr('opacity', applyOpacityValue(hoverLine, priceSummaryItem))
            .attr('d', calcArea);
        });
        // }
      }
    }

    const dataArrays = [historicalData, p95Data, p5Data, forecastData];

    // Calculate max from all historical and forecast lines
    const maxYValue = getMaxValueFromArrayOfLines(dataArrays);
    const minYValue = getMinValueFromArrayOfLines(dataArrays);
    if (maxYValue > maxY) {
      maxY = maxYValue;
    }
    if (minYValue < minY) {
      minY = minYValue;
    }
  }); // end of lines loop
  return { lineDrawFunctions, minY, maxY };
};

const checkActiveLineGraphItemsLength = (activeLineGraphItems: ILineGraphItem[]) => {
  return activeLineGraphItems.length === 0;
};

const checkHistoryAndForecastLength = (history: ISummaryHistory[], forecast: ISummaryForecast[]) => {
  return !history?.length || (!history && !forecast);
};

const validateHistoryEndValue = (itemEnd: Date, historyEnd: null | Date = null) => {
  return historyEnd === null || itemEnd > historyEnd;
};

const validateBeginValue = (begin: Date | null, itemBegin: Date) => {
  return begin === null || itemBegin < begin;
};

const validateEndValue = (end: Date | null, itemEnd: Date) => {
  return end === null || itemEnd > end;
};

const checkMinYValue = (minY: number) => {
  return minY >= 0;
};

const formatTooltipPriceValueText = (y0: number) => {
  return formatNumber(y0, Math.abs(y0) < priceValueTextNumber ? 1 : 0);
};

const checkHistoryEndValue = (historyEnd: null | Date = null) => {
  return historyEnd ?? 0;
};

const checkDateRangeForContractPeriodLabel = (dateRange: EDateRange) => {
  return dateRange === EDateRange.CONTRACT_PERIOD;
};

const processScaleRangeOfData = (
  activeLineGraphItemsProcessed: ILineGraphItem[],
  display: EDisplay,
  svg: Selection<SVGGElement, unknown, null, undefined>,
  x: ScaleTime<number, number>,
  y: ScaleLinear<number, number>,
  width: number,
  height: number
) => {
  // Scale range of data (both history + forecast)
  let begin: null | Date = null;
  let end: null | Date = null;
  let historyEnd: null | Date = null;

  activeLineGraphItemsProcessed.forEach(item => {
    const price = item.priceSummary[display];
    if (!price) {
      renderNoDataGraph({ svg, x, y, width, height });
      return;
    }

    const { history, forecast } = price;

    // check for non-exitent history or forecast
    if (checkHistoryAndForecastLength(history, forecast)) {
      renderNoDataGraph({ svg, x, y, width, height });
      return;
    }

    // use forecast data to establish bounds if no forecast exists
    const itemBegin = moment(history[0].x, 'MM/YYYY').toDate();
    let itemEnd = moment(history[history.length - 1].x, 'MM/YYYY').toDate();
    if (validateHistoryEndValue(itemEnd, historyEnd)) {
      historyEnd = itemEnd;
    }
    if (forecast.length) {
      itemEnd = moment(forecast[forecast.length - 1].x, 'MM/YYYY').toDate();
    }
    if (validateBeginValue(begin, itemBegin)) {
      begin = itemBegin;
    }
    if (validateEndValue(end, itemEnd)) {
      end = itemEnd;
    }
  });

  if (!begin || !end) {
    return;
  }
  x.domain([begin, end]);
  return historyEnd;
};

// line-graph-price-${label}
function getActiveState(label: string, defaultValue: boolean): boolean {
  const storageKey = `line-graph-price-${label}`;
  const storageValue = localStorage.getItem(storageKey);

  if (!storageValue) {
    localStorage.setItem(storageKey, JSON.stringify(defaultValue));
    return defaultValue;
  }

  const storageValueParsed = JSON.parse(storageValue);
  if (typeof storageValueParsed !== 'boolean') {
    return defaultValue;
  }

  return storageValueParsed;
}

function handleDisplayChange(d: EDisplay, setDisplay: Function) {
  setDisplay(d);
}

// Update local storage state using label name
function toggleActiveStateByLabel(label: string, lineGraphItems: ILineGraphItem[], setLineGraphItems: Function) {
  const localStorageLabel = `line-graph-price-${label}`;
  const oldValue = JSON.parse(localStorage.getItem(localStorageLabel) ?? '');
  if (typeof oldValue === 'boolean') {
    localStorage.setItem(localStorageLabel, JSON.stringify(!oldValue));
  }

  const lineGraphItemsUpdated = lineGraphItems.map(item => {
    if (item.label === label) {
      return {
        ...item,
        active: !item.active,
      };
    }
    return item;
  });
  setLineGraphItems(lineGraphItemsUpdated);
}

function renderPriceButtons(lineGraphItems: ILineGraphItem[], setLineGraphItems: Function, setHoverLine: Function) {
  return lineGraphItems.map(item => (
    <BubblyCheckboxButton
      key={`price-button-${item.label}`}
      color={item.color}
      background={item.background}
      text={item.label}
      active={item.active}
      onClick={() => toggleActiveStateByLabel(item.label, lineGraphItems, setLineGraphItems)}
      subtext={item.priceSummary.description}
      onHover={() => setHoverLine(item.label)}
      onBlur={() => setHoverLine('')}
    />
  ));
}

const getLineGraphItems = (productSummary: ISummaryProduct) => {
  const lineGraphItemsNew: ILineGraphItem[] = [];
  let colorIndex = 0;
  let bgcolorIndex = 0;
  // Contract
  lineGraphItemsNew.push({
    label: 'Contract',
    active: getActiveState('Contract', true),
    color: lineColors[colorIndex],
    background: bgColors[bgcolorIndex],
    priceSummary: productSummary.contract,
  });

  colorIndex = colorIndex + 1;
  bgcolorIndex = bgcolorIndex + 1;

  // Contract w/o options
  lineGraphItemsNew.push({
    label: contractWithoutOptions,
    active: getActiveState(contractWithoutOptions, false),
    color: lineColors[colorIndex],
    background: bgColors[bgcolorIndex],
    priceSummary: productSummary.withoutOpts ?? productSummary.contract,
  });

  colorIndex = colorIndex + 1;
  bgcolorIndex = bgcolorIndex + 1;

  // BATNA 1
  lineGraphItemsNew.push({
    label: 'BATNA 1',
    active: getActiveState('BATNA 1', true),
    color: lineColors[colorIndex],
    background: bgColors[bgcolorIndex],
    priceSummary: productSummary.mainBatna,
  });

  colorIndex = colorIndex + 1;
  bgcolorIndex = bgcolorIndex + 1;

  // Other BATNAs
  if (productSummary.otherBatnas.length) {
    productSummary.otherBatnas.forEach((otherBatna, index) => {
      lineGraphItemsNew.push({
        label: `BATNA ${index + otherBatnaNumber}`,
        active: getActiveState(`BATNA ${index + otherBatnaNumber}`, false),
        color: lineColors[colorIndex],
        background: bgColors[bgcolorIndex],
        priceSummary: otherBatna,
      });
      colorIndex = colorIndex + 1;
      bgcolorIndex = bgcolorIndex + 1;
    });
  }
  return lineGraphItemsNew;
};

// Note: Temporary solution for tooltip
function mousemove(
  svg: Selection<SVGGElement, unknown, null, undefined>,
  tooltip: Selection<SVGGElement, unknown, null, undefined>,
  x: ScaleTime<number, number>,
  y: ScaleLinear<number, number>,
  width: number,
  height: number,
  pricePerUnitLabel: string
) {
  const mousePos = mouse(event.target);
  const x0 = x.invert(mousePos[0]); // this is the given x value on graph
  const y0 = y.invert(mousePos[1]);

  // Redraw after ever mousemove event
  svg
    .select('.x-line')
    .attr('x1', x(x0))
    .attr('y1', 0)
    .attr('x2', x(x0))
    .attr('y2', height);

  svg
    .select('.y-line')
    .attr('x1', 0)
    .attr('y1', y(y0))
    .attr('x2', width)
    .attr('y2', y(y0));

  tooltip.style('display', null);
  tooltip.attr('transform', `translate(${x(x0)}, ${y(y0)})`);
  tooltip.select('.price-date').text(dateFormatter(x0));
  tooltip.select('.price-value').text(`${formatTooltipPriceValueText(y0)} ${pricePerUnitLabel}`);
  tooltip.raise(); // bring tooltip on top of guide lines
}

const processVerticalPadding = (y: ScaleLinear<number, number>, minY: number, maxY: number) => {
  // Determine if vertical padding is needed within negative y-axis
  if (checkMinYValue(minY)) {
    y.domain([0, maxY + maxY * verticalPaddingMultiplier]);
  } else {
    // Calculate standard buffer
    // Take the delta (max - min) and then multiply this by a percentage
    // THEN add this to max, and subtract from min
    const deltaYValue = maxY - minY;
    const verticalPadding = deltaYValue * verticalPaddingMultiplier;
    y.domain([minY - verticalPadding, maxY + verticalPadding]);
  }
};

export default ({
  periodStart,
  periodEnd,
  productSummary,
  pricePerUnitLabel,
  dealName,
  selectedCostLabel,
  selectedForecastLabel,
  productItemId,
}: IMultiLineGraphProps) => {
  const lineGraphRef = useRef<HTMLDivElement>(null);

  const [display, setDisplay] = useState(EDisplay.NETBACK);
  const [dateRange, setDateRange] = useState(EDateRange.CONTRACT_PERIOD);
  const [lineGraphItems, setLineGraphItems] = useState<ILineGraphItem[]>([]);
  const [activeLineGraphItems, setActiveLineGraphItems] = useState<ILineGraphItem[]>([]);
  const [hoverLine, setHoverLine] = useState('');
  const [showUncertaintyRange, setShowUncertaintyRange] = useState(true);

  // generate unique class name for svg container
  const uniqueLineGraphClassName = `${lineGraphClassName}-${productItemId}`;

  // Filter out inactive items
  useEffect(() => {
    setActiveLineGraphItems(lineGraphItems.filter(item => item.active));
  }, [lineGraphItems]);

  // Initialize the line graph items
  useEffect(() => {
    setLineGraphItems(getLineGraphItems(productSummary));
  }, [productSummary]);

  useEffect(() => {
    // Calculate dimensions
    const dimensions = getMultiLineGraphDimensions(lineGraphRef);
    const width = dimensions.width - margin.left - margin.right;

    // Clear old SVG
    selectAll(`.${uniqueLineGraphClassName} > *`).remove();

    // Create container
    const svg = select(lineGraphRef.current)
      .append('svg')
      .style('font-size', font.size)
      .style(fontFamily, font.family)
      .attr('width', width + margin.right + margin.left)
      .attr('height', dimensions.height)
      .style('cursor', 'none')
      .attr(dataTestId, multiLineGraphContainer)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Draw caption text
    const lineGraphCaptionText = generateLegendText({
      dealName,
      product: productSummary.name,
      periodStart,
      periodEnd,
      selectedForecastLabel,
      selectedCostLabel,
      display,
    });

    const captionBottom = dimensions.height - margin.top - captionBottomNumber;
    svg
      .append('text')
      .attr('x', 0)
      .attr('y', captionBottom)
      .attr(dataTestId, multiLineGraphLabel)
      .text(lineGraphCaptionText);

    // Draw legend
    const legendHorizontalPadding = 40;
    const legendHeight = drawLegend(
      svg,
      activeLineGraphItems,
      captionBottom - legendHorizontalPadding,
      width,
      showUncertaintyRange
    );

    // Now that we know the space needed for the legend and the caption, we can calculate chart area height
    const height = captionBottom - legendHeight - strokeWidthMinSize * legendHorizontalPadding;

    // Set the ranges
    const x = scaleTime().range([0, width]);
    const y = scaleLinear().range([height, 0]);
    if (checkActiveLineGraphItemsLength(activeLineGraphItems)) {
      renderNoDataGraph({ svg, x, y, width, height });
      return;
    }

    // Process historical data based on selection
    const activeLineGraphItemsProcessed = processHistoricalDataBasedOnSelection(
      activeLineGraphItems,
      { periodStart, periodEnd, dateRange, display },
      svg,
      x,
      y,
      width,
      height
    );

    // Track all lines to render
    // Process each line
    const { lineDrawFunctions, minY, maxY } = processEachLine(
      activeLineGraphItemsProcessed,
      display,
      svg,
      x,
      y,
      hoverLine,
      showUncertaintyRange
    );

    // Scale range of data (both history + forecast)
    const historyEnd = processScaleRangeOfData(activeLineGraphItemsProcessed, display, svg, x, y, width, height);
    // Determine if vertical padding is needed within negative y-axis
    processVerticalPadding(y, minY, maxY);

    // Y-axis
    svg
      .append('g')
      .style('font-size', font.size)
      .style(fontFamily, font.family)
      .call(
        axisLeft(y)
          .tickPadding(tickPaddingValue)
          .tickArguments([tickArgumentsValue])
          .tickFormat(d => `${String(getTickFormat(d))} ${pricePerUnitLabel}`)
      );

    // X-axis (draw after y-axis to allow the y-axis scale to be updated)
    svg
      .append('g')
      .attr('transform', `translate(0, ${y(0)})`)
      .style('font-size', font.size)
      .style(fontFamily, font.family)
      .call(
        axisBottom(x)
          // @ts-ignore
          .tickFormat(timeFormat(xAxisDateFormat))
          .tickPadding(tickPaddingValue)
      );

    // Draw lines
    lineDrawFunctions.forEach((fn: Function) => fn());

    // Hover tooltip
    const tooltip = svg
      .append('g')
      .attr('class', 'tooltip')
      .style('display', 'none');

    tooltip
      .append('path')
      .attr(
        'd',
        symbol()
          .type(symbolTriangle)
          .size(tooltipD3Size)
      )
      .attr('fill', transparentize(tooltipTransparentizeValue, colors.darkBackgroundElevation1))
      .attr('transform', 'rotate(180) translate(0, 8)');

    tooltip
      .append('rect')
      .attr('width', tooltipWidthAttrValue)
      .attr('height', tooltipHeightAttr)
      .attr('x', tooltipXAttrValue)
      .attr('y', tooltipOuterYAttrValue)
      .attr('rx', legendStrokeWidth)
      .attr('ry', legendStrokeWidth);

    tooltip
      .append('text')
      .attr('class', 'price-date')
      .attr(textAnchor, 'middle')
      .attr('y', tooltipInnerFirstYAttrValue)
      .attr('fill', 'white');

    tooltip
      .append('text')
      .attr('class', 'price-value')
      .attr(textAnchor, 'middle')
      .attr('y', tooltipInnerSecondYAttrValue)
      .attr('fill', 'white');

    svg
      .append('line')
      .attr('x1', x(0))
      .attr('y1', 0)
      .attr('x2', x(0))
      .attr('y2', height)
      .style(strokeWidth, svgStrokeWidthValue)
      .style('stroke', '#000000')
      .style(strokeDashArray, '2,3')
      .attr('class', 'x-line')
      .attr(pointerEvents, 'none');

    svg
      .append('line')
      .attr('x1', 0)
      .attr('y1', y(0))
      .attr('x2', width)
      .attr('y2', y(0))
      .style(strokeWidth, svgStrokeWidthValue)
      .style('stroke', '#000000')
      .style(strokeDashArray, '2,3')
      .attr('class', 'y-line')
      .attr(pointerEvents, 'none');

    if (!showUncertaintyRange) {
      svg
        .append('line')
        .attr('x1', x(checkHistoryEndValue(historyEnd)))
        .attr('y1', 0)
        .attr('x2', x(checkHistoryEndValue(historyEnd)))
        .attr('y2', height)
        .style(strokeWidth, 1)
        .style('stroke', colors.globalIcon)
        .style(strokeDashArray, '5,5')
        .attr(pointerEvents, 'none');

      svg
        .append('text')
        .attr(textAnchor, 'end')
        .attr('x', x(checkHistoryEndValue(historyEnd)) - svgAttrNumber)
        .attr('y', svgYAttrValue)
        .text('History');
    }

    // Create contract period fill area
    svg
      .append('rect')
      .attr('x', x(periodStart))
      .attr('width', x(periodEnd) - x(periodStart))
      .attr('height', height)
      .attr('fill', transparentize(svgTransparentizeValue, colors.darkBackgroundElevation1));

    // Contract period label
    if (checkDateRangeForContractPeriodLabel(dateRange)) {
      svg
        .append('text')
        .attr(textAnchor, 'middle')
        .attr('x', (x(periodStart) + x(periodEnd)) / strokeWidthMinSize)
        .attr('y', svgYAttrValue)
        .text('Contract period');
    } else {
      svg
        .append('text')
        .attr(textAnchor, 'start')
        .attr('x', x(periodStart) + svgAttrNumber)
        .attr('y', svgYAttrValue)
        .text('Contract');

      svg
        .append('text')
        .attr(textAnchor, 'start')
        .attr('x', x(periodStart) + svgAttrNumber)
        .attr('y', svgYAttrMaxValue)
        .text('period');
    }

    // Overlay to capture mouse events
    svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('width', width)
      .attr('height', height)
      .on('mouseover', () => {
        // show tooltip and cross hairs
        tooltip.style('display', null);
        svg.select('.x-line').style('stroke', '#000000');
        svg.select('.y-line').style('stroke', '#000000');
      })
      .on('mouseout', () => {
        // hide tooltip and cross hairs
        tooltip.style('display', 'none');
        svg.select('.x-line').style('stroke', 'transparent');
        svg.select('.y-line').style('stroke', 'transparent');
      })
      .on('mousemove', () => mousemove(svg, tooltip, x, y, width, height, pricePerUnitLabel));
  }, [
    activeLineGraphItems,
    periodStart,
    periodEnd,
    pricePerUnitLabel,
    dateRange,
    display,
    dealName,
    productSummary.name,
    uniqueLineGraphClassName,
    hoverLine,
    showUncertaintyRange,
    selectedForecastLabel,
    selectedCostLabel,
  ]);

  const multiToggleButtonOptions = [
    { text: 'Base', onClick: () => handleDisplayChange(EDisplay.BASE, setDisplay) },
    { text: 'Revenue', onClick: () => handleDisplayChange(EDisplay.SALES, setDisplay) },
    { text: 'Netback', onClick: () => handleDisplayChange(EDisplay.NETBACK, setDisplay) },
    { text: 'Margin', onClick: () => handleDisplayChange(EDisplay.MARGIN, setDisplay) },
  ];

  return (
    <div>
      <Controls>
        <ControlGroup>
          <Control>
            <MultiToggleButton
              label="LINE ITEM"
              indexActive={getActiveIndexForMultiToggleButton(display)}
              options={multiToggleButtonOptions}
            />
          </Control>
          <Control>
            <ToggleButton
              label="PERIOD"
              isPrimaryOptionActive={dateRange === EDateRange.CONTRACT_PERIOD}
              primaryOptionText="Contract Period"
              secondaryOptionText="All History"
              onPrimaryOptionClick={() => setDateRange(EDateRange.CONTRACT_PERIOD)}
              onSecondaryOptionClick={() => setDateRange(EDateRange.ALL)}
            />
          </Control>
          <Control>
            <ToggleButton
              label="UNCERTAINTY"
              isPrimaryOptionActive={showUncertaintyRange}
              primaryOptionText="Show"
              secondaryOptionText="Hide"
              onPrimaryOptionClick={() => setShowUncertaintyRange(true)}
              onSecondaryOptionClick={() => setShowUncertaintyRange(false)}
            />
          </Control>
        </ControlGroup>
        <PriceContainer>
          <PriceLabel>PRICE</PriceLabel>
          <BubblyContainer>{renderPriceButtons(lineGraphItems, setLineGraphItems, setHoverLine)}</BubblyContainer>
        </PriceContainer>
      </Controls>
      <div className={uniqueLineGraphClassName} ref={lineGraphRef} />
    </div>
  );
};
